import React, { createContext, useContext, useState, useEffect } from 'react';
import { db } from '../firebase/Config';
import { collection, getDocs, doc, getDoc, addDoc, updateDoc, deleteDoc } from 'firebase/firestore';

const ProductContext = createContext();

export const ProductProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch all products
  const fetchProducts = async () => {
    try {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, 'products'));
      const productsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setProducts(productsData);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  // Fetch single product by ID
  const fetchProductById = async (id) => {
    try {
      
      const docRef = doc(db, 'products', id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        
        return { id: docSnap.id, ...docSnap.data() };
      } else {
        
        throw new Error('Product not found');
      }
    } catch (err) {
      setError(err.message);
      
      throw err;
    }
  };

  // Add new product
  const addProduct = async (productData) => {
    try {
      setLoading(true);
      const docRef = await addDoc(collection(db, 'products'), productData);
      await fetchProducts(); // Refresh the products list
      setLoading(false);
      return docRef.id;
    } catch (err) {
      setError(err.message);
      setLoading(false);
      throw err;
    }
  };

  // Update product
  const updateProduct = async (id, updatedData) => {
    try {
      setLoading(true);
      const productRef = doc(db, 'products', id);
      await updateDoc(productRef, updatedData);
      await fetchProducts(); // Refresh the products list
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
      throw err;
    }
  };

  // Delete product
  const deleteProduct = async (id) => {
    try {
      setLoading(true);
      await deleteDoc(doc(db, 'products', id));
      await fetchProducts(); // Refresh the products list
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
      throw err;
    }
  };

  // Initial fetch when provider mounts
  useEffect(() => {
    fetchProducts();
  }, []);

  return (
    <ProductContext.Provider
      value={{
        products,
        loading,
        error,
        fetchProducts,
        fetchProductById,
        addProduct,
        updateProduct,
        deleteProduct
      }}
    >
      {children}
    </ProductContext.Provider>
  );
};

export const useProducts = () => {
  const context = useContext(ProductContext);
  if (!context) {
    throw new Error('useProducts must be used within a ProductProvider');
  }
  return context;
};